import { CommissionStatuses } from 'constants/commissionStatuses';
import { CommissionTypes } from 'constants/commissionTypes';
import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import { blue } from '@mui/material/colors';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider } from 'react-admin';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditDialogButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowDialogButton,
} from 'shared/mui/ContextMenu';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { Link } from 'shared/mui/Link/Link';
import { DatagridBody } from 'shared/react-admin/DatagridBodies/DatagridBody/DatagridBody';
import { DatagridCommissionsRow } from 'shared/react-admin/DatagridRows/DatagridCommissionsRow/DatagridCommissionsRow';
import { DatagridRow } from 'shared/react-admin/DatagridRows/DatagridRow/DatagridRow';
import { DatagridStickyContainer } from 'shared/react-admin/Datagrids/DatagridSticky/DatagridStickyContainer';
import {
  ChipField,
  CurrencyField,
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  NumberField,
  SingleFieldList,
  TooltipTextField,
} from 'shared/react-admin/Fields';
import { CommissionStatusChipField } from 'shared/react-admin/Fields/CommissionStatusChipField/CommissionStatusChipField';
import { IconButtonStyled } from 'shared/react-admin/Fields/CommissionStatusChipField/CommissionStatusChipField.styled';
import { ReferenceArrayField } from 'shared/react-admin/ReferenceFields';
import { CommissionsCommentDialog } from 'shared/widgets/CommissionsCommentDialog/CommissionsCommentDialog';
import { Commission } from 'types/embedded/commission';
import { Permission } from 'types/embedded/permission';
import { getId } from 'utils';

import { DatagridStyled } from './MerchantShowCommissionsListDatagrid.styled';
import { MERCHANT_COMMISSIONS_PREFERENCE_KEY } from '../MerchantShowCommissionsList';

type Props = {
  handleOpenShowDialog: (record?: Commission) => void;
  handleOpenEditDialog: (record?: Commission) => void;
  handleOpenDeleteDialog: (record?: Commission) => void;
};

export const MerchantShowCommissionsListDatagrid: React.FC<Props> = ({
  handleOpenEditDialog,
  handleOpenDeleteDialog,
  handleOpenShowDialog,
}) => {
  const translate = useTypedTranslate();

  const { hasResourceAccess, hasResourcePermissions } = usePermissions();
  const { appliedFilters } = useFilterContext();

  const {
    record: recordComment,
    handleOpenRecordModal: handleOpenRecordModalComment,
    openRecordModal: openRecordModalComment,
    handleCloseRecordModal: handleCloseRecordModalComment,
  } = useRecordModal<Commission>();

  return (
    <>
      <DatagridStickyContainer>
        <DatagridStyled
          body={
            <DatagridBody
              row={
                !appliedFilters?.id && !appliedFilters?.status?.length ? (
                  <DatagridCommissionsRow />
                ) : (
                  <DatagridRow />
                )
              }
            />
          }
          bulkActionButtons={false}
          preferenceKey={MERCHANT_COMMISSIONS_PREFERENCE_KEY}
        >
          <FunctionField
            label="ID"
            render={(record: Commission) => (
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenShowDialog(record);
                }}
              >
                {getId(record?.originId)}
              </Link>
            )}
            sortBy="id"
            source="originId"
          />
          <FunctionField
            label={translate('app.merchantCommissions.type')}
            render={(record: Commission) => {
              if (record.typeEnum.value === CommissionTypes.Child) {
                return `\u27A5 ${translate('app.merchantCommissions.child')}`;
              }
              return record.typeEnum.description;
            }}
            sortable={false}
            source="type"
            sx={{ textWrap: 'nowrap' }}
          />
          <TooltipTextField
            label={translate('app.table.name')}
            onClick={handleOpenShowDialog}
            source="name"
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
              color: blue[700],
            }}
          />
          <FunctionField
            label={translate('app.table.status')}
            render={(record: Commission) => {
              return (
                <RecordContextProvider value={record?.status}>
                  <CommissionStatusChipField source="description">
                    {Boolean(record?.commentCount) && (
                      <IconButtonStyled
                        onClick={() => {
                          handleOpenRecordModalComment(record);
                        }}
                      >
                        <ModeCommentOutlinedIcon />
                      </IconButtonStyled>
                    )}
                  </CommissionStatusChipField>
                </RecordContextProvider>
              );
            }}
            sortable={false}
            source="status"
          />
          {hasResourceAccess(Resources.Providers) && (
            <FunctionField
              cellClassName="column-provider"
              label={translate('app.table.provider')}
              render={(record: Commission) => record?.provider?.name ?? EMPTY_FIELD}
            />
          )}
          {hasResourceAccess(Resources.Currencies) && (
            <FunctionField
              cellClassName="column-currency"
              label={translate('app.table.currency')}
              render={(record: Commission) => (
                <RecordContextProvider value={record?.currency}>
                  <CurrencyField
                    alphaCodeSource="alphaCode"
                    label={translate('app.table.currency')}
                    nameSource="name"
                  />
                </RecordContextProvider>
              )}
            />
          )}
          {hasResourceAccess('transaction_types') && (
            <ReferenceArrayField
              label={translate('app.commissions.transactionTypes')}
              reference="transaction_types"
              sortable={false}
              source="transactionTypes"
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" />
              </SingleFieldList>
            </ReferenceArrayField>
          )}
          <FunctionField
            cellClassName="column-percent"
            label={translate('app.commissions.percent')}
            render={(record: Commission) => {
              if (!record?.rate?.percent) {
                return EMPTY_FIELD;
              }

              return `${record?.rate?.percent}%`;
            }}
            source="rate.percent"
          />
          <NumberField
            cellClassName="column-fix"
            emptyText={EMPTY_FIELD}
            label={translate('app.merchantCommissions.fix')}
            sortable={false}
            source="rate.fix"
          />
          <NumberField
            cellClassName="column-max"
            emptyText={EMPTY_FIELD}
            label={translate('app.merchantCommissions.max')}
            sortable={false}
            source="rate.max"
          />
          <NumberField
            cellClassName="column-min"
            emptyText={EMPTY_FIELD}
            label={translate('app.merchantCommissions.min')}
            sortable={false}
            source="rate.min"
          />
          <EmbeddedFunctionField
            cellClassName="column-calculationMethod"
            label={translate('app.commissions.calculationMethod')}
            sortable={false}
            sourceField="calculationMethod.description"
          />
          <DateTimeField label={translate('app.table.createdAt')} source="createdAt" />
          <DateTimeField label={translate('app.table.updatedAt')} source="updatedAt" />
          <ContextMenu
            render={(record: Commission) => {
              const isShowButton = record?.status
                ? [CommissionStatuses.Created, CommissionStatuses.Rejected].includes(
                    record?.status?.value,
                  )
                : false;

              return [
                hasResourceAccess(Resources.UserActions) && (
                  <ContextMenuEntityUserActionsButton
                    entityName="commission"
                    key="user-action-link"
                  />
                ),
                <ContextMenuShowDialogButton
                  key="show-button"
                  onClick={() => handleOpenShowDialog(record)}
                />,
                isShowButton &&
                  hasResourcePermissions(Resources.Commissions, [
                    Permission.Full,
                    Permission.Update,
                  ]) && (
                    <ContextMenuEditDialogButton
                      key="edit-button"
                      onClick={() => handleOpenEditDialog(record)}
                    />
                  ),
                isShowButton &&
                  hasResourcePermissions(Resources.Commissions, [
                    Permission.Full,
                    Permission.Delete,
                  ]) && (
                    <ContextMenuDeleteButton
                      key="delete-button"
                      onClick={() => handleOpenDeleteDialog(record)}
                    />
                  ),
              ];
            }}
          />
        </DatagridStyled>
      </DatagridStickyContainer>
      <CommissionsCommentDialog
        onClose={handleCloseRecordModalComment}
        open={openRecordModalComment}
        record={recordComment}
      />
    </>
  );
};
